import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { pickErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { notifyError, notifySuccess } from "@/lib/notify";
import DataTable from "@/components/DataTable";

const ALL_CLASSES = "__all__";

/** Parse "10A" -> { number: 10, section: "A" }. Numeric class first so the
 *  DataTable's default sort orders 8 < 9 < 10 correctly (not lexicographic). */
function parseClass(className) {
  const s = String(className || "");
  const m = s.match(/^(\d+)?\s*([A-Za-z]*)/);
  return {
    number: m && m[1] ? parseInt(m[1], 10) : 9999,
    section: m ? (m[2] || "").toUpperCase() : "",
  };
}

function classSortKey(row) {
  const c = parseClass(row.class_name);
  // Encode as "0009|A" so alpha-sort matches numeric intent.
  return `${String(c.number).padStart(6, "0")}|${c.section}`;
}

function rollSortKey(row) {
  const n = parseInt(String(row.roll_number ?? ""), 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// School-friendly DOB: "26-Apr-2011". Input from backend is ISO "YYYY-MM-DD".
// Falls back to the original string if we can't parse (never blanks a row).
function formatDob(iso) {
  if (!iso) return "—";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  const [, y, mm, dd] = m;
  const idx = parseInt(mm, 10) - 1;
  if (idx < 0 || idx > 11) return String(iso);
  return `${dd}-${MONTHS_SHORT[idx]}-${y}`;
}

export default function Students() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const schoolId = admin?.school_id;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [classFilter, setClassFilter] = useState(ALL_CLASSES);

  // Add-student form
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [className, setClassName] = useState("");
  const [rollNumber, setRollNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // View Progress modal
  const [progressModal, setProgressModal] = useState(null); // {studentId, studentName}
  const [progressRows, setProgressRows] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/school/students");
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(pickErr(err, "Failed to load students"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const classOptions = useMemo(() => {
    const set = new Set(list.map((s) => s.class_name).filter(Boolean));
    return Array.from(set).sort((a, b) =>
      classSortKey({ class_name: a }).localeCompare(
        classSortKey({ class_name: b })
      )
    );
  }, [list]);

  const filtered = useMemo(() => {
    // Default multi-key sort: class (numeric) → section → roll → name.
    const rows =
      classFilter === ALL_CLASSES
        ? [...list]
        : list.filter((s) => s.class_name === classFilter);
    rows.sort((a, b) => {
      const ka = classSortKey(a);
      const kb = classSortKey(b);
      if (ka !== kb) return ka.localeCompare(kb);
      const ra = rollSortKey(a);
      const rb = rollSortKey(b);
      if (ra !== rb) return ra - rb;
      return (a.name || "").localeCompare(b.name || "");
    });
    return rows;
  }, [list, classFilter]);

  async function onCreate(e) {
    e.preventDefault();
    setFormError("");
    if (!schoolId) {
      setFormError("Missing school context — please re-login.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/students", {
        name: name.trim(),
        dob,
        class_name: className.trim(),
        roll_number: rollNumber.trim(),
        school_id: schoolId,
      });
      setName("");
      setDob("");
      setClassName("");
      setRollNumber("");
      notifySuccess("Student added");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to create student");
      setFormError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onUpload(e) {
    e.preventDefault();
    setUploadError("");
    setUploadResult(null);
    const file = fileInput.current?.files?.[0];
    if (!file) {
      setUploadError("Please choose a CSV file");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/students/bulk-upload", fd);
      setUploadResult(data);
      if (fileInput.current) fileInput.current.value = "";
      notifySuccess(
        `Bulk upload: ${data.created_count} created, ${data.skipped_count} skipped`
      );
      await load();
    } catch (err) {
      const msg = pickErr(err, "Upload failed");
      setUploadError(msg);
      notifyError(msg);
    } finally {
      setUploading(false);
    }
  }

  async function openProgress(student) {
    setProgressModal({
      studentId: student.id,
      studentName: student.name,
    });
    setProgressLoading(true);
    setProgressRows([]);
    try {
      const [{ data: tests }, { data: cats }] = await Promise.all([
        api.get(`/student/${student.id}/tests`),
        api.get("/categories?active_only=true"),
      ]);
      const nameById = Object.fromEntries(
        (Array.isArray(cats) ? cats : []).map((c) => [c.id, c.name])
      );
      setProgressRows(
        (Array.isArray(tests) ? tests : []).map((t) => ({
          category_id: t.category_id,
          category_name: nameById[t.category_id] || "Archived assessment",
          status: t.status,
        }))
      );
    } catch (err) {
      notifyError(pickErr(err, "Failed to load progress"));
      setProgressModal(null);
    } finally {
      setProgressLoading(false);
    }
  }

  // --------------------------------------------------------------------
  // DataTable column config — accessors drive both sorting AND search.
  // --------------------------------------------------------------------
  const columns = useMemo(
    () => [
      {
        key: "student_code",
        label: "Student Code",
        accessor: (r) => r.student_code || "",
        className: "text-slate-900 font-mono",
      },
      {
        key: "class_name",
        label: "Class",
        accessor: (r) => r.class_name || "",
        // Use numeric-aware key for sort so 9 < 10 < 11.
        render: (r) => r.class_name || "—",
      },
      {
        key: "roll_number",
        label: "Roll No.",
        accessor: (r) => rollSortKey(r),
        render: (r) => r.roll_number || "—",
      },
      {
        key: "name",
        label: "Student Name",
        accessor: (r) => r.name || "",
        className: "text-slate-900",
      },
      {
        key: "dob",
        label: "DOB",
        // Sort chronologically by using the ISO string as the accessor;
        // rendered in "26-Apr-2011" school-friendly format. Search also
        // matches on the ISO source, so admins can find via 2011 etc.
        accessor: (r) => r.dob || "",
        render: (r) => formatDob(r.dob),
        className: "text-slate-700 tabular-nums whitespace-nowrap",
      },
      {
        key: "assigned_count",
        label: "Assigned",
        accessor: (r) => r.assigned_count ?? 0,
        align: "right",
        className: "text-slate-700 tabular-nums",
      },
      {
        key: "completed_count",
        label: "Completed",
        accessor: (r) => r.completed_count ?? 0,
        align: "right",
        className: "text-emerald-700 tabular-nums",
      },
      {
        key: "pending_count",
        label: "Pending",
        accessor: (r) => r.pending_count ?? 0,
        align: "right",
        className: "text-slate-500 tabular-nums",
      },
    ],
    []
  );

  const actions = useCallback(
    (s) => (
      <>
        <button
          type="button"
          onClick={() => navigate(`/school/reports?student_id=${s.id}`)}
          data-testid={`students-action-reports-${s.id}`}
          className="text-xs text-blue-700 hover:text-blue-900 underline mr-3"
        >
          View Reports
        </button>
        <button
          type="button"
          onClick={() => navigate(`/school/assign-test?student_id=${s.id}`)}
          data-testid={`students-action-assign-${s.id}`}
          className="text-xs text-blue-700 hover:text-blue-900 underline mr-3"
        >
          Assign Test
        </button>
        <button
          type="button"
          onClick={() => openProgress(s)}
          data-testid={`students-action-progress-${s.id}`}
          className="text-xs text-slate-700 hover:text-slate-900 underline"
        >
          View Progress
        </button>
      </>
    ),
    [navigate]
  );

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Students</h2>
      <p className="text-sm text-slate-500 mb-6">
        Add students one-by-one or upload a CSV.
      </p>

      {/* ---- Add-student form (unchanged) ---- */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Add a student
        </h3>
        <form
          onSubmit={onCreate}
          className="grid sm:grid-cols-4 gap-3 items-end"
          data-testid="students-create-form"
        >
          <div>
            <label className="block text-xs text-slate-600 mb-1">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="students-create-name"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">DoB</label>
            <input
              type="date"
              required
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              data-testid="students-create-dob"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Class</label>
            <input
              required
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              data-testid="students-create-class"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Roll number
            </label>
            <input
              required
              value={rollNumber}
              onChange={(e) => setRollNumber(e.target.value)}
              data-testid="students-create-roll"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="sm:col-span-4 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="students-create-submit"
              className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? "Creating…" : "Add student"}
            </button>
            {formError ? (
              <p
                data-testid="students-create-error"
                className="text-sm text-red-600"
              >
                {formError}
              </p>
            ) : null}
          </div>
        </form>
      </section>

      {/* ---- Bulk CSV upload (unchanged) ---- */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Bulk upload (CSV)
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Required columns: <code>name, dob, class_name, roll_number</code>
        </p>
        <form
          onSubmit={onUpload}
          className="flex flex-wrap items-center gap-3"
          data-testid="students-bulk-form"
        >
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            data-testid="students-bulk-file"
            className="text-sm"
          />
          <button
            type="submit"
            disabled={uploading}
            data-testid="students-bulk-submit"
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
          {uploadError ? (
            <p
              data-testid="students-bulk-error"
              className="text-sm text-red-600"
            >
              {uploadError}
            </p>
          ) : null}
        </form>

        {uploadResult ? (
          <div
            data-testid="students-bulk-result"
            className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700"
          >
            <div>
              Created: <strong>{uploadResult.created_count}</strong> · Skipped:{" "}
              <strong>{uploadResult.skipped_count}</strong>
            </div>
            {uploadResult.errors && uploadResult.errors.length ? (
              <ul className="mt-2 list-disc list-inside text-red-700">
                {uploadResult.errors.map((er, i) => (
                  <li key={i}>
                    Row {er.row}: {er.error}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ---- Roster (DataTable) ---- */}
      <DataTable
        testid="students"
        columns={columns}
        rows={filtered}
        loading={loading}
        error={error}
        getRowId={(r) => r.id}
        emptyMessage="No students match."
        defaultSort={null /* rows are pre-sorted class → section → roll → name */}
        exportFilename="students"
        toolbar={
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            data-testid="students-filter-class"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm bg-white"
          >
            <option value={ALL_CLASSES}>All classes</option>
            {classOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        }
        actions={actions}
      />

      {/* ---- View Progress modal ---- */}
      {progressModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          data-testid="students-progress-modal"
          onClick={() => setProgressModal(null)}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg bg-white shadow-lg"
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <h4 className="text-sm font-semibold text-slate-900">
                Assessment progress
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                {progressModal.studentName}
              </p>
            </div>
            <div className="px-5 py-4 space-y-2 text-sm">
              {progressLoading ? (
                <p className="text-xs text-slate-500">Loading…</p>
              ) : progressRows.length === 0 ? (
                <p className="text-xs text-slate-500">No assignments yet.</p>
              ) : (
                <ul
                  className="text-xs space-y-1"
                  data-testid="students-progress-list"
                >
                  {progressRows.map((row) => (
                    <li
                      key={row.category_id}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="text-slate-800">
                        {row.category_name}
                      </span>
                      <span
                        className={
                          "capitalize tabular-nums " +
                          (row.status === "completed"
                            ? "text-emerald-700"
                            : row.status === "started"
                              ? "text-amber-700"
                              : "text-slate-500")
                        }
                      >
                        {row.status || "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 text-right">
              <button
                type="button"
                onClick={() => setProgressModal(null)}
                data-testid="students-progress-close"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
